const MAX_IMAGES_FULL_INCLUSION = 5;
const DELTA_FILLING_PERCENT = 5;

const toAnomalyCount = (item) => {
  const value = Number(item?.anomalies);
  return Number.isFinite(value) ? value : 0;
};

const sameReading = (a, b) =>
  a === b || (a?._time != null && a._time === b?._time);

/**
 * Dedupe by filling_level, skipping levels already reserved (first/last).
 */
const deduplicateByFillingLevel = (items, reservedLevels = new Set()) => {
  const seen = new Set(reservedLevels);
  return items.filter((item) => {
    const level = item.filling_level;
    if (seen.has(level)) return false;
    seen.add(level);
    return true;
  });
};

/**
 * Default PDF image preselection for compliance / characterization reports:
 * - first and last of the cycle are always kept
 * - ≤ 5 images: keep all (after filling_level dedupe on the middle)
 * - > 5: also keep significant filling delta (≥ 5) and increase in sorting errors
 * - dedupe by identical filling_level (without dropping first/last)
 */
export const getUniqueHistoryCycle = (historyCycle) => {
  const sorted = [...(historyCycle || [])].sort(
    (a, b) => new Date(a._time) - new Date(b._time),
  );
  if (sorted.length === 0) return [];
  if (sorted.length === 1) return sorted;

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const middle = sorted.slice(1, -1);

  let middleSelected;
  if (sorted.length <= MAX_IMAGES_FULL_INCLUSION) {
    middleSelected = middle;
  } else {
    middleSelected = middle.filter((item, middleIndex) => {
      const index = middleIndex + 1; // index in sorted
      const hasSignificantDelta = item.diff_filling >= DELTA_FILLING_PERCENT;
      const hasMoreSortingErrors =
        toAnomalyCount(item) > toAnomalyCount(sorted[index - 1]);
      return hasSignificantDelta || hasMoreSortingErrors;
    });
  }

  const reservedLevels = new Set([first.filling_level, last.filling_level]);
  const dedupedMiddle = deduplicateByFillingLevel(
    middleSelected,
    reservedLevels,
  );

  if (sameReading(first, last)) return [first, ...dedupedMiddle];
  return [first, ...dedupedMiddle, last];
};
